import React from 'react';
import { motion } from 'framer-motion';
import { LogOut } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface WhatsappDisconnectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  instanceName: string;
}

const WhatsappDisconnectModal: React.FC<WhatsappDisconnectModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  instanceName,
}) => {
  const { t } = useTranslation();
  
  if (!isOpen) return null;

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      onClick={onClose}
    >
      <motion.div
        className="relative w-full max-w-sm rounded-xl bg-white dark:bg-gray-800 shadow-2xl border border-gray-200 dark:border-gray-700/50 p-6 flex flex-col gap-6"
        initial={{ y: 50, opacity: 0, scale: 0.95 }}
        animate={{ y: 0, opacity: 1, scale: 1 }}
        exit={{ y: 50, opacity: 0, scale: 0.95 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex flex-col gap-1 items-center text-center">
          <LogOut className="size-8 text-red-600 dark:text-red-400 mb-2" />
          <h2 className="text-xl font-bold text-[#181411] dark:text-white">{t('integrations.whatsapp_disconnect_title')}</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
            {t('integrations.whatsapp_disconnect_message', { instanceName })}
          </p>
        </div>

        {/* Footer / Actions */}
        <div className="flex justify-end gap-3 pt-2">
          <motion.button
            type="button"
            onClick={onClose}
            className="flex h-12 w-full sm:w-auto cursor-pointer items-center justify-center overflow-hidden rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-text-gray-200 text-sm font-bold leading-normal tracking-[0.015em] px-6 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {t('modals.cancel')}
          </motion.button>
          
          <motion.button
            type="button"
            onClick={onConfirm}
            className="flex h-12 w-full sm:w-auto cursor-pointer items-center justify-center overflow-hidden rounded-lg bg-red-600 text-white gap-2 text-sm font-bold leading-normal tracking-[0.015em] px-6 hover:bg-red-700 transition-colors"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <LogOut className="size-5" />
            {t('integrations.whatsapp_button_disconnect')}
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default WhatsappDisconnectModal;